/**
 * Runtime-agnostic HTML sanitizer with a strict tag/attribute allowlist.
 *
 * Works identically on the server (Cloudflare Worker, no DOM) and in the
 * browser, so SSR output and client hydration always match. Only used for
 * admin-authored rich text.
 */

const ALLOWED_TAGS = new Set([
  "p", "br", "strong", "em", "b", "i", "u",
  "h1", "h2", "h3", "h4",
  "ul", "ol", "li",
  "blockquote", "a",
]);

const VOID_TAGS = new Set(["br"]);

const SAFE_URL = /^(https?:|mailto:|tel:|#|\/)/i;

function escapeText(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(s: string) {
  return escapeText(s).replace(/"/g, "&quot;");
}

function buildAnchorAttrs(rawAttrs: string): string | null {
  const hrefMatch = rawAttrs.match(/href\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
  const href = (hrefMatch?.[2] ?? hrefMatch?.[3] ?? hrefMatch?.[4] ?? "").trim();
  if (!href || !SAFE_URL.test(href)) return null;
  const external = /^https?:/i.test(href);
  return ` href="${escapeAttr(href)}"${external ? ' target="_blank" rel="noopener noreferrer nofollow"' : ""}`;
}

export function sanitizeHtml(input: string): string {
  // Drop anything that can execute or embed, including its content.
  let html = input.replace(
    /<(script|style|iframe|object|embed|svg|math|template|noscript)\b[\s\S]*?<\/\1\s*>/gi,
    "",
  );
  html = html.replace(/<!--[\s\S]*?-->/g, "");

  const open: string[] = [];
  let out = "";
  let cursor = 0;
  const tagRe = /<\/?([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;
  let match: RegExpExecArray | null;

  while ((match = tagRe.exec(html)) !== null) {
    out += escapeText(html.slice(cursor, match.index));
    cursor = match.index + match[0].length;

    const tag = match[1].toLowerCase();
    const isClosing = match[0].startsWith("</");
    if (!ALLOWED_TAGS.has(tag)) continue;

    if (VOID_TAGS.has(tag)) {
      if (!isClosing) out += `<${tag} />`;
      continue;
    }

    if (isClosing) {
      const idx = open.lastIndexOf(tag);
      if (idx === -1) continue;
      while (open.length > idx) out += `</${open.pop()}>`;
      continue;
    }

    if (tag === "a") {
      const attrs = buildAnchorAttrs(match[2] ?? "");
      if (attrs === null) continue;
      out += `<a${attrs}>`;
    } else {
      out += `<${tag}>`;
    }
    open.push(tag);
  }

  out += escapeText(html.slice(cursor));
  while (open.length) out += `</${open.pop()}>`;
  return out;
}
